import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import SearchPanel from "@widgets/SearchPanel";
import SnTab from "@shared/components/SnTab";
import { SnSearchBtn } from "@shared/components/SnSearchBtn";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnButton } from "@shared/components/SnButton";
import SnLabel from "@shared/components/SnLabel";
import SnNumberBox from "@shared/components/SnNumberBox";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_SCT_TAX_REG_001_POP_03 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();

    const searchRef = useRef<any>(null);
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
      grid3: React.createRef(),
    });

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });

    const [popupParent, setPopupParent] = useState<any>(null);
    const [selectedTabPage, setTapPage] = useState<string>("1");
    const [um, setUm] = useState<number>(0);

    useImperativeHandle(ref, () => ({
      onSearch,
      checkValidity: async () => {
        let isSame: boolean = false;
        const checkedRows =
          selectedTabPage === "1"
            ? gridRefs.current["grid1"].current.getSelectedRows()
            : gridRefs.current["grid2"].current.getCheckedRows();
        if (checkedRows.length <= 1) return true;

        const cdCustValue = checkedRows[0].cd_cust; // 외주처

        isSame = checkedRows.every((item: any) => item.cd_cust === cdCustValue);

        if (!isSame) {
          await showDialog("alter", "외주처가 동일한 건만 적용 가능합니다.");
          return false;
        } else {
          return true;
        }
      },
      getOkayData: () => {
        let result;
        if (selectedTabPage === "1") {
          result = [gridRefs.current["grid1"].current?.getSelectedRows() || []];
        } else {
          const headerList =
            gridRefs.current["grid2"].current?.getCheckedRows() || [];
          const lineList =
            gridRefs.current["grid3"].current?.getCheckedRows() || [];

          const merged = lineList.map((l: any) => {
            const h = headerList.find((x: any) => x.no_work === l.no_work);
            return {
              ...h,
              ...l,
            };
          });

          result = [merged || []];
        }
        return {
          rtnData: result,
        };
      },
    }));

    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_SCT_TAX_REG_001_POP_03",
            {}
          );
          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormControlData(rtn.formControlData);
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
            grid2: rtn.grid.grid2,
            grid3: rtn.grid.grid3,
          });
        } catch (error) {}
      };
      fetchform();
    }, []);

    const onSearch = async () => {
      try {
        const params = {
          id: "sn_sct_tax_reg_001_pop_001_03",
          params: searchRef.current.getFormdata(),
        };

        gridRefs.current["grid1"].current.resetUpdate();
        gridRefs.current["grid2"].current.resetUpdate();
        gridRefs.current["grid3"].current.resetUpdate();
        const data: any = await fnGetDataSet(params);
        setDataSources({
          grid1: data[0] || [],
          grid2: data[1] || [],
          grid3: data[2] || [],
        });
      } catch (error) {}
    };

    const onCellValueChanged = async (event: CellValueChangedEvent) => {
      if (!event) return;
      const colId = event.colId;
      const rowData = event.rowData;
      const rowNode = event.rowNode;

      if (colId === "qt" || colId === "um") {
        const qt = rowData.qt ?? 0;
        const um = rowData.um ?? 0;
        const am = qt * um;
        rowNode.setDataValue("am", am);
      }
    };

    const onTabChanged = (index: string) => {
      setTapPage(index);
    };

    const onCheckedRowChanged1 = (
      value: boolean,
      type: "row" | "header" = "row"
    ) => {
      if (type === "header") {
        gridRefs.current["grid3"].current.setCheckedAll(value);
      } else {
        gridRefs.current["grid3"].current.setCheckedAllWithFilter(value);
      }
    };

    const onCheckedRowChanged2 = (value: boolean) => {
      const checkedRows =
        gridRefs.current["grid3"].current.getCheckedFilteredRows();
      if (checkedRows.length > 0)
        gridRefs.current["grid2"].current.setCheckedFocusedRow(true, "noEvent");
      else
        gridRefs.current["grid2"].current.setCheckedFocusedRow(
          false,
          "noEvent"
        );
    };

    const changeUm = (value: number) => {
      setUm(value);
    };

    const changeUmBatch1 = () => {
      let targetList: any[] =
        gridRefs.current["grid1"].current.getSelectedRows() || [];
      if (targetList.length === 0) {
        showDialog("alter", "선택된 행이 없습니다.");
        return;
      }

      for (const t of targetList) {
        const node = gridRefs.current["grid1"].current.getRowNode(t.KeyField);
        if (node) {
          node.setDataValue("um", um);
        }
      }
    };

    const changeUmBatch2 = () => {
      let targetList: any[] =
        gridRefs.current["grid2"].current.getCheckedRows();
      if (targetList.length === 0) {
        showDialog("alter", "선택된 행이 없습니다.");
        return;
      }

      for (const t of targetList) {
        const node = gridRefs.current["grid2"].current.getRowNode(t.KeyField);
        if (node) {
          node.setDataValue("um", um);
        }
      }
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: { onCellValueChanged: onCellValueChanged },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCellValueChanged: onCellValueChanged,
            onCheckedRowChaged: onCheckedRowChanged1,
          },
        },
        grid3: {
          columns: columns.grid3,
          dataSource: dataSources.grid3,
          events: { onCheckedRowChaged: onCheckedRowChanged2 },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div className="h-full flex flex-col">
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div className="mb-2" style={{ textAlign: "center" }}>
          <SnSearchBtn
            text={"조회"}
            onClick={onSearch}
            sx={{ width: 80, marginTop: 1.5 }}
            spinner={true}
          />
        </div>

        <div className="flex-1 h-full min-h-0 space-y-1.5">
          <SnTab
            titles={["원단", "제품"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            header={[
              <div className="relative flex space-x-4">
                <SnLabel text="단가" type="AfterLabel" />
                <div>
                  <SnNumberBox
                    defaultValue={um}
                    isEditable={true}
                    onValueChanged={changeUm}
                    format={"#.####"}
                  />
                </div>
                <SnButton text={"단가일괄입력"} onClick={changeUmBatch1} />
              </div>,
              <div className="relative flex space-x-4">
                <SnLabel text="단가" type="string" />
                <div>
                  <SnNumberBox
                    defaultValue={um}
                    isEditable={true}
                    onValueChanged={changeUm}
                    format={"#.####"}
                  />
                </div>
                <SnButton text={"단가일괄입력"} onClick={changeUmBatch2} />
              </div>,
            ]}
            body={[
              <SnGrid
                ref={gridRefs.current["grid1"]}
                columnConfig={grids.grid1.columns}
                rowData={grids.grid1.dataSource}
                etcProps={{
                  rowSelection: "multiple",
                  popupParent: popupParent,
                }}
                events={grids.grid1.events}
              />,

              <div className="h-full flex flex-col gap-y-1.5">
                <div className="min-h-[230px]">
                  <SnGrid
                    ref={gridRefs.current["grid2"]}
                    columnConfig={grids.grid2.columns}
                    rowData={grids.grid2.dataSource}
                    etcProps={{
                      rowSelection: "single",
                      ischeckboxSelection: true,
                      popupParent: popupParent,
                    }}
                    events={grids.grid2.events}
                    filter={{
                      refs: [gridRefs.current["grid3"]],
                      columns: ["no_work"],
                    }}
                  />
                </div>
                <div className="min-h-[230px]">
                  <SnGrid
                    ref={gridRefs.current["grid3"]}
                    columnConfig={grids.grid3.columns}
                    rowData={grids.grid3.dataSource}
                    etcProps={{
                      rowSelection: "single",
                      ischeckboxSelection: true,
                      popupParent: popupParent,
                    }}
                    events={grids.grid3.events}
                  />
                </div>
              </div>,
            ]}
          ></SnTab>
        </div>
      </div>
    );
  }
);

export default SN_SCT_TAX_REG_001_POP_03;
